import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { IrisDMSFileI } from '@iris/common/modules/dms/models/IrisDMSFile';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { IrisDmsFilesSandbox } from '@iris/common/modules/dms/sandbox/dms-files.sandbox';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ToastService } from '@ngx-strakit/cdk/controls/toast';
import {
  adjustValidForCustom,
  getFormControlValue,
  getLinkDuration,
  isShareLinkExternalFormValid$,
  isValueForSetToCustom,
} from '@iris/common/modules/dms/utils/dms-form-group.utils';
import { take, tap } from 'rxjs';
import {
  SkRadioMode,
  StrakitButtonComponent,
  StrakitNumberComponent,
  StrakitRadioInputComponent,
  StrakitSelectComponent,
  StrakitToggleComponent,
} from '@ngx-strakit/cdk/controls';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IrisCopyFieldComponent } from '@iris/common/modules/copy-field/copy-field.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { StrakitButtonPrefixDirective } from '@ngx-strakit/cdk';

@Component({
  selector: 'iris-dms-file-share-link-external',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,

    MatTooltipModule,

    StrakitSelectComponent,
    StrakitToggleComponent,
    StrakitNumberComponent,
    StrakitRadioInputComponent,

    IrisCopyFieldComponent,
    StrakitButtonComponent,
    StrakitButtonPrefixDirective,
  ],
  templateUrl: './file-share-link-external.component.html',
  styleUrls: ['./file-share-link-external.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrisFileShareLinkExternalComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly dmsFilesSandBox = inject(IrisDmsFilesSandbox);
  private readonly translateService = inject(TranslateService);
  private readonly toastService = inject(ToastService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly file = input<IrisDMSFileI>();

  readonly validityValues = this.dmsFilesSandBox.getFileLinkValidityValues();
  readonly downloadValues = this.dmsFilesSandBox.getFileLinkLimitDownloadValues();

  readonly permanentLinkMode = SkRadioMode.TextAndIcon;

  readonly permanentLinkOptions = [
    {
      value: true,
      title: this.translateService.instant('label.dms.LinkCurrentVersion'),
      tooltip: this.translateService.instant('text.dms.CurrentVersion'),
    },
    {
      value: false,
      title: this.translateService.instant('label.dms.KeepUpdatedToLatestVersion') ,
      tooltip: this.translateService.instant('text.dms.LatestVersion'),
    },
  ];

  readonly form = this.fb.group({
    validFor: new FormControl(1),
    validForCustom: new FormControl(-1),
    allowSingleDownloading: new FormControl(false),
    isPermanentLink: new FormControl(true),
    isSilent: new FormControl(false),
  });

  readonly isFormValid = toSignal(isShareLinkExternalFormValid$(this.form));

  readonly generatedLink = signal(null);
  isLinkGenerationInProcess = false;

  ngOnInit(): void {
    this.form.controls.validFor.valueChanges.pipe(
      tap(() => adjustValidForCustom(this.form)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  get isValidForCustom(): boolean {
    return isValueForSetToCustom(this.form);
  }

  getControlValue<T = unknown>(field: string): T {
    return getFormControlValue<T>(this.form)(field);
  }

  generateLink(): void {
    this.isLinkGenerationInProcess = true;
    this.dmsFilesSandBox.getFileDownloadLink$(
      this.file(),
      getLinkDuration(this.form),
      this.getControlValue('isPermanentLink'),
      this.getControlValue('isSilent'),
      this.getControlValue('allowSingleDownloading'),
    ).pipe(
      take(1),
      tap((link) => {
        this.isLinkGenerationInProcess = false;
        this.generatedLink.set(link);
        this.changeDetectorRef.markForCheck();
      }),
    ).subscribe();
  }

  onCopyLink(): void {
    this.toastService.success({ message: this.translateService.instant('text.dms.FileShareLinkCopied') });
  }
}
